class ReusableModal extends HTMLElement {
  constructor () {
    super()
    this.open = false
  }

  connectedCallback () {
    this.setupListeners()

    const modal = this.querySelector('.reusable-modal')
    const modalHeader = this.querySelector('.reusable-modal__header')
    const modalBody = this.querySelector('.reusable-modal__body')
    const modalClose = this.querySelector('.reusable-modal__close')

    this.defaultStyles = {
      modalMaxWidth: modal.style.maxWidth || '',
      modalClasses: [...modal.classList],
      modalHeaderClasses: [...modalHeader.classList],
      modalBodyClasses: [...modalBody.classList],
      modalCloseClasses: [...modalClose.classList],
    }
  }

  setupListeners () {
    window.addEventListener('open:modal', this.handleOpen.bind(this))
    window.addEventListener('close:modal', this.handleClose.bind(this))
    this.closeButton.addEventListener('click', this.handleClose.bind(this))
    this.addEventListener('click', this.handleOutsideClick.bind(this))
    document.addEventListener('keydown', this.handleKeyDown.bind(this))
  }

  handleOrientationChange () {
    // Detect orientation change

    const video = this.querySelector('video') // Target your video element

    // Check if orientation is landscape
    if (screen.orientation.type.startsWith('landscape')) {
      // If in landscape mode, request fullscreen
      if (video.requestFullscreen) {
        video.requestFullscreen()
      } else if (video.webkitRequestFullscreen) { // For Safari/iOS
        video.webkitRequestFullscreen()
      } else if (video.msRequestFullscreen) { // For IE/Edge
        video.msRequestFullscreen()
      }
    } else {
      // Exit fullscreen on portrait mode
      if (document.exitFullscreen) {
        document.exitFullscreen()
      } else if (document.webkitExitFullscreen) { // For Safari/iOS
        document.webkitExitFullscreen()
      } else if (document.msExitFullscreen) { // For IE/Edge
        document.msExitFullscreen()
      }
    }
  }

  handleOpen (event) {
    this.open = true
    const detail = event.detail || {}
    this.applyStyles(detail)
    window.dispatchEvent(new CustomEvent('show:page-obscure'))
    window.dispatchEvent(new CustomEvent('prevent:page-scroll'))

    const modalBody = this.querySelector('.reusable-modal__body')
    const focusableElements = this.getFocusableElements(modalBody)
    if (focusableElements.length) {
      focusableElements[0].focus()
    }

    // Add orientation change listener
    screen.orientation.addEventListener('change', this.handleOrientationChange.bind(this))

    // Fix for iOS Safari z-index issues
    this.fixIOSZIndexIssues()
  }

  fixIOSZIndexIssues () {
    const modal = this.querySelector('.reusable-modal')
    const modalHeader = this.querySelector('.reusable-modal__header')
    const modalClose = this.querySelector('.reusable-modal__close')

    // Force hardware acceleration and create a new stacking context
    if (modalHeader.classList.contains('absolute')) {
      modalHeader.style.transform = 'translateZ(0)'
      modalHeader.style.willChange = 'transform'
      modalHeader.style.zIndex = '999' // Use a very high z-index

      if (modalClose) {
        modalClose.style.transform = 'translateZ(0)'
        modalClose.style.willChange = 'transform'
        modalClose.style.zIndex = '1000' // Even higher z-index
      }

      // Ensure the modal container has a 3D context
      modal.style.transformStyle = 'preserve-3d'
    }
  }

  applyStyles (detail) {
    const modal = this.querySelector('.reusable-modal')
    const modalHeader = this.querySelector('.reusable-modal__header')
    const modalBody = this.querySelector('.reusable-modal__body')
    const modalFooter = this.querySelector('.reusable-modal__footer')
    const modalClose = this.querySelector('.reusable-modal__close')

    if (detail.width) {
      modal.style.maxWidth = detail.width
    }

    if (detail.padding === 'none') {
      modal.classList.remove('p-4', 'md:p-4')
    } else if (detail.padding) {
      modal.classList.add(...detail.padding)
    }

    if (detail.modalClassAdd) {
      modal.classList.add(...detail.modalClassAdd)
    }

    if (detail.modalClassRemove) {
      modal.classList.remove(...detail.modalClassRemove)
    }

    if (detail.headerClassAdd) {
      modalHeader.classList.add(...detail.headerClassAdd)
    }
    if (detail.headerClassRemove) {
      modalHeader.classList.remove(...detail.headerClassRemove)
    }

    if (detail.bodyClassAdd) {
      modalBody.classList.add(...detail.bodyClassAdd)
    }
    if (detail.bodyClassRemove) {
      modalBody.classList.remove(...detail.bodyClassRemove)
    }

    if (detail.closeClassAdd) {
      modalClose.classList.add(...detail.closeClassAdd)
    }
    if (detail.closeClassRemove) {
      modalClose.classList.remove(...detail.closeClassRemove)
    }

    // Add footer class handling
    if (detail.footerClassAdd) {
      modalFooter.classList.add(...detail.footerClassAdd)
    }
    if (detail.footerClassRemove) {
      modalFooter.classList.remove(...detail.footerClassRemove)
    }
  }

  handleClose () {
    this.open = false
    window.dispatchEvent(new CustomEvent('allow:page-scroll'))
    setTimeout(() => {
      this.resetStyles()
      this.clearModal()
    }, 250)
    window.dispatchEvent(new CustomEvent('hide:page-obscure'))

    // Remove orientation change listener
    screen.orientation.removeEventListener('change', this.handleOrientationChange.bind(this))
  }

  resetStyles () {
    const modal = this.querySelector('.reusable-modal')
    const modalHeader = this.querySelector('.reusable-modal__header')
    const modalClose = this.querySelector('.reusable-modal__close')

    modal.style.maxWidth = this.defaultStyles.modalMaxWidth
    modal.className = ''
    modal.classList.add(...this.defaultStyles.modalClasses)

    // Reset iOS fixes
    modal.style.transformStyle = ''

    modalHeader.className = ''
    modalHeader.classList.add(...this.defaultStyles.modalHeaderClasses)
    modalHeader.style.transform = ''
    modalHeader.style.willChange = ''
    modalHeader.style.zIndex = ''

    modalClose.className = ''
    modalClose.classList.add(...this.defaultStyles.modalCloseClasses)
    modalClose.style.transform = ''
    modalClose.style.willChange = ''
    modalClose.style.zIndex = ''
  }

  handleOutsideClick (event) {
    if (!this.open) return
    const modalContainer = this.querySelector('.reusable-modal')
    if (this.open && !modalContainer.contains(event.target)) {
      this.handleClose()
    }
  }

  handleKeyDown (event) {
    if (!this.open) return

    const modal = this.querySelector('.reusable-modal')
    const focusableElements = this.getFocusableElements(modal)
    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    if (event.key === 'Tab') {
      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          event.preventDefault()
          lastElement.focus()
        }
      } else {
        if (document.activeElement === lastElement) {
          event.preventDefault()
          firstElement.focus()
        }
      }
    } else if (event.key === 'Escape') {
      this.handleClose()
    }
  }

  clearModal () {
    this.querySelector('.reusable-modal__title').innerHTML = ''
    this.querySelector('.reusable-modal__body').innerHTML = ''
    this.querySelector('.reusable-modal__footer').innerHTML = ''
    this.querySelector('.reusable-modal__body').scrollTo({ top: 0, behavior: 'instant' })
  }

  getFocusableElements (container) {
    return container.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
  }

  set open (value) {
    this.setAttribute('open', value)
  }

  get open () {
    return this.hasAttribute('open')
  }

  get closeButton () {
    return this.querySelector('.reusable-modal__close')
  }
}

if (!customElements.get('reusable-modal')) {
  customElements.define('reusable-modal', ReusableModal)
}
